import moment from "moment";
import { canJoinDocAlbum, getMediaAlbumId, groupConsecutiveDocs, isSameMediaSend } from "./documentAlbum";

const PROTO_POLLUTION_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const safeSpread = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const clean = {};
  for (const key of Object.keys(obj)) {
    if (!PROTO_POLLUTION_KEYS.has(key)) {
      clean[key] = obj[key];
    }
  }
  return clean;
};

/** Loose id compare — socket/API ids are often strings while optimistic rows use numbers. */
export const idsMatch = (a, b) => {
  if (a == null || b == null) return false;
  const sa = String(a);
  const sb = String(b);
  if (sa === "" || sb === "") return false;
  return sa === sb;
};

export const isSameChatMessage = (local, incoming) => {
  if (!local || !incoming) return false;
  if (incoming.messageRequestId && local.messageRequestId && idsMatch(incoming.messageRequestId, local.messageRequestId)) {
    return true;
  }
  if (incoming.id && local.id && idsMatch(incoming.id, local.id)) return true;
  if (incoming.id && local.messageRequestId && idsMatch(incoming.id, local.messageRequestId)) return true;
  if (incoming.messageRequestId && local.id && idsMatch(incoming.messageRequestId, local.id)) return true;
  if (
    local.local &&
    local.messageType &&
    incoming.messageType &&
    local.messageType === incoming.messageType &&
    incoming.mediaAlbumId &&
    local.mediaAlbumId &&
    idsMatch(incoming.mediaAlbumId, local.mediaAlbumId) &&
    incoming.attachedFileName &&
    local.attachedFileName &&
    String(incoming.attachedFileName) === String(local.attachedFileName)
  ) {
    return true;
  }
  return false;
};

// ---------- Clean transcript: strip AI-generated HTML guide / list blocks ----------
export const cleanTranscript = (text) => {
  if (!text || typeof text !== 'string') return text;
  let cleaned = text
    .replace(/<ul[\s>][\s\S]*?<\/ul>/gi, '')
    .replace(/<ol[\s>][\s\S]*?<\/ol>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
  return cleaned || text;
};

const INVALID_TRANSCRIPT_PATTERNS = [
  "transcription_not_possible",
  "i'm unable to access",
  "cannot transcribe",
  "unable to transcribe",
  "transcription failed",
  "could not transcribe",
  "not able to transcribe",
  "audio cannot be transcribed",
  "i cannot transcribe",
  "sorry, i cannot",
  "unable to process",
  "failed to process",
  "i'm sorry, but i don't have access",
  "please provide the spoken words",
  "can't transcribe audio directly",
  "cannot transcribe audio directly",
  "provide the spoken words",
  "text version of the audio",
  "i'm tired",
  "please, sir",
  "i can't help",
  "i don't have the ability",
  "no audio provided",
  "no speech detected",
  "audio is not clear",
  "audio file is empty",
  "i cannot listen",
  "i cannot hear",
  "i'm not able to listen",
  "as an ai",
  "as a language model",
  "as a text-based",
];

export const isInvalidTranscript = (text) => {
  if (!text || typeof text !== 'string') return false;
  const lower = text.toLowerCase();
  return INVALID_TRANSCRIPT_PATTERNS.some(p => lower.includes(p));
};

// ---------- Strip markdown from display names ----------
export const stripMarkdown = (text) => {
  if (typeof text !== 'string') return '';
  if (!text) return '';
  return text
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/\*(.+?)\*/g, '$1');
};

/** Match chat-bubble list rendering: markdown `- ` / `* ` / `+ ` → `• `. */
export const markdownListMarkersToBullets = (text) => {
  if (typeof text !== 'string' || !text) return text || '';
  return text.replace(/(^|\n)([ \t]*)([-*+])[ \t]+/g, '$1$2• ');
};

/** Plain-text quote used in the reply composer (strip HTML, show round bullets). */
export const formatReplyQuoteText = (message, maxLength = 300) => {
  if (message == null || message === '') return '';
  let text = String(message)
    .replace(/<br\s*[/]?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ');
  text = markdownListMarkersToBullets(text);
  if (maxLength && text.length > maxLength) {
    text = `${text.slice(0, maxLength)}...`;
  }
  return text;
};

const GROUP_NAME_MAX_LENGTH = 30;
export const truncateGroupName = (name) => {
  if (!name || typeof name !== 'string') return name || '';
  return name.length > GROUP_NAME_MAX_LENGTH
    ? name.slice(0, GROUP_NAME_MAX_LENGTH) + '…'
    : name;
};

/** Remove HTML tags and decode common entities; suitable for plain-text summaries (e.g. email → todo). */
export const stripHtmlToPlainText = (input) => {
  if (input == null || input === '') return '';
  let s = String(input)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ');
  s = s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|table|h[1-6]|li|section|article|header|footer|blockquote)>/gi, '\n')
    .replace(/<\/?(p|div|tr|table|h[1-6]|li|section|article|header|footer|blockquote)[^>]*>/gi, '\n');
  s = s.replace(/<[^>]+>/g, ' ');
  s = s
    .replace(/&#x([\da-fA-F]+);/gi, (_, hex) => {
      const code = parseInt(hex, 16);
      return code > 0 && code < 0x110000 ? String.fromCharCode(code) : '';
    })
    .replace(/&#(\d+);/g, (_, num) => {
      const code = parseInt(num, 10);
      return code > 0 && code < 0x110000 ? String.fromCharCode(code) : '';
    })
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
  s = s
    .replace(/\s+\n/g, '\n')
    .replace(/\n\s+/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return s;
};

// ---------- Scroll constants ----------
export const SCROLL_THRESHOLD = 200;

export const isNearBottom = (container) => {
  if (!container) return false;
  return container.scrollTop + SCROLL_THRESHOLD >=
    (container.scrollHeight - container.clientHeight);
};

/**
 * Centres a message in its chat's scroll container only. Unlike
 * scrollIntoView, which also scrolls every scrollable ancestor (the page and
 * the panels) and so pushed the chat header out of view.
 */
export const scrollMessageIntoChat = (el, { behavior = 'smooth', container } = {}) => {
  if (!el) return;
  const box = container || el.closest('[id^="chat-sc-"]');
  if (!box) return;
  const boxRect = box.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const top = box.scrollTop + (elRect.top - boxRect.top) - (boxRect.height - elRect.height) / 2;
  box.scrollTo({ top: Math.max(0, top), behavior });
};

// Longest a freshly opened chat stays pinned to its newest message.
const SETTLE_MAX_MS = 5000;

/**
 * Opens a chat on its newest message and keeps it there while images,
 * previews and late messages grow the history. Call it before paint (from a
 * layout effect) so the first frame already shows the bottom. The pin
 * re-applies from a ResizeObserver, which runs after layout and before paint,
 * so growth never shows as a jump.
 *
 * `phaseRef.current` stays true while pinned (pagination waits on it). The
 * pin ends after SETTLE_MAX_MS or as soon as the user scrolls themselves;
 * wheel, touch, keys and scrollbar drags count, layout-shift scroll events do not.
 * Returns the cleanup.
 */
export const pinChatToBottom = (container, phaseRef) => {
  const pin = () => {
    if (phaseRef.current) container.scrollTop = container.scrollHeight;
  };

  phaseRef.current = true;
  container.scrollTop = container.scrollHeight;

  const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(pin) : null;
  if (observer) {
    observer.observe(container);
    Array.from(container.children).forEach((child) => observer.observe(child));
  }

  const release = () => {
    phaseRef.current = false;
  };
  const releaseOnScrollKey = (e) => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) release();
  };
  const releaseOnScrollbar = (e) => {
    // Only a press on the scrollbar itself, not a click on a message.
    if (e.target === container && e.offsetX > container.clientWidth) release();
  };
  container.addEventListener('wheel', release, { passive: true });
  container.addEventListener('touchmove', release, { passive: true });
  container.addEventListener('keydown', releaseOnScrollKey);
  container.addEventListener('mousedown', releaseOnScrollbar);
  const settleTimeout = setTimeout(release, SETTLE_MAX_MS);

  return () => {
    clearTimeout(settleTimeout);
    if (observer) observer.disconnect();
    container.removeEventListener('wheel', release);
    container.removeEventListener('touchmove', release);
    container.removeEventListener('keydown', releaseOnScrollKey);
    container.removeEventListener('mousedown', releaseOnScrollbar);
  };
};

// Check if message content is only emoji (no regular text)
export const isMessageEmojiOnly = (msg) => {
  const text = typeof msg === 'string' ? msg : (msg?.message ?? '');
  if (!text || typeof text !== 'string') return false;
  const cleaned = text.replace(/<br\s*[/]?>/gi, ' ').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (cleaned.length === 0) return false;
  const withoutEmoji = cleaned.replace(/[\p{Extended_Pictographic}\p{Emoji_Modifier}\u200D\uFE0F\uFE0E\s]/gu, '');
  return withoutEmoji.length === 0;
};

// ---------- Merge incoming server message with local optimistic message ----------
export const mergeIncomingMessage = (localMsg, serverData) => {
  const serverFileUrl = serverData.fileUrl;
  const localFileUrl = localMsg.fileUrl;
  const localImages = localMsg.images;
  const isLocalImage = localMsg.local === true;
  const isVideo = localMsg.messageType === "video" || serverData.messageType === "video";

  const serverHasValidUrl = serverFileUrl &&
    (Array.isArray(serverFileUrl)
      ? serverFileUrl.length > 0 && serverFileUrl[0] && serverFileUrl[0] !== ''
      : serverFileUrl !== '');

  const merged = {
    ...safeSpread(localMsg),
    ...safeSpread(serverData),
    local: isVideo && serverHasValidUrl ? false : (isLocalImage ? true : (serverHasValidUrl ? false : localMsg.local)),
    serverFileUrl: serverHasValidUrl ? serverFileUrl : localMsg.serverFileUrl,
    animated: true,
    transcriptionPending: serverData.transcribedAudio ? false : localMsg.transcriptionPending,
  };

  if ((!serverData.message || serverData.message === '') && localMsg.message) {
    merged.message = localMsg.message;
  }

  // Own optimistic rows keep the plaintext caption. The socket echo is still
  // ciphertext, and CryptoJS encrypt("") is a non-empty string that would
  // otherwise look like a caption and split the gallery.
  if (localMsg.local === true && typeof localMsg.message === "string") {
    const serverMsg = serverData.message;
    if (typeof serverMsg === "string" && serverMsg.startsWith("U2FsdGVkX1")) {
      merged.message = localMsg.message;
    }
  }

  if (localMsg.isLastImage != null) merged.isLastImage = localMsg.isLastImage;
  if (localMsg.hasMultiple != null) merged.hasMultiple = localMsg.hasMultiple;

  if (!merged.mediaAlbumId && localMsg.mediaAlbumId) {
    merged.mediaAlbumId = localMsg.mediaAlbumId;
  }

  if (isVideo && serverHasValidUrl) {
    merged.fileUrl = serverFileUrl;
    merged.local = false;
    if (serverData.thumbnailUrl) merged.thumbnailUrl = serverData.thumbnailUrl;
  } else if (isLocalImage && localFileUrl) {
    merged.fileUrl = localFileUrl;
  } else if (!serverHasValidUrl && localFileUrl) {
    merged.fileUrl = localFileUrl;
  }

  if (localImages && localImages.length > 0) {
    if (!merged.images || merged.images.length === 0) {
      merged.images = localImages;
    } else if (isLocalImage) {
      merged.images = localImages.map((localImg, idx) => {
        const serverImg = merged.images[idx] || {};
        return {
          ...localImg,
          local: true,
          serverFileUrl: serverImg.fileUrl || localImg.serverFileUrl
        };
      });
    } else if (serverHasValidUrl) {
      merged.images = merged.images.map((img, idx) => {
        const localImg = localImages[idx] || {};
        const sFileUrl = img.fileUrl;
        const lFileUrl = localImg.fileUrl;
        const hasValid = sFileUrl &&
          (Array.isArray(sFileUrl)
            ? sFileUrl.length > 0 && sFileUrl[0] && sFileUrl[0] !== ''
            : sFileUrl !== '');
        return {
          ...localImg,
          ...img,
          fileUrl: hasValid ? sFileUrl : lFileUrl,
          local: hasValid ? false : localImg.local
        };
      });
    } else {
      merged.images = localImages;
    }
  }

  if (merged.images) {
    merged.count = merged.images.length;
  }

  return merged;
};

// ---------- Message normalization helpers ----------
export const normalizeImageArray = (msg) => {
  const baseTs = msg.createdAtTs ?? new Date(msg.createdAt || Date.now()).getTime();
  if (msg.images && Array.isArray(msg.images) && msg.images.length > 0) {
    return msg.images.map((img, idx) => {
      const imgTs =
        img.createdAtTs ??
        (img.createdAt ? new Date(img.createdAt).getTime() : baseTs + idx * 0.1);
      return { ...img, createdAtTs: imgTs };
    });
  }
  if (!msg.fileUrl) return [];
  const urls = Array.isArray(msg.fileUrl) ? msg.fileUrl : [msg.fileUrl];
  return urls.map((url, idx) => ({
    ...msg,
    fileUrl: url,
    createdAtTs: baseTs + idx * 0.1,
  }));
};

export const normalizeFlatMessage = (msg) => {
  const createdAtTs = msg.createdAtTs ?? new Date(msg.createdAt || Date.now()).getTime();
  const images = normalizeImageArray({ ...msg, createdAtTs });
  return { ...msg, createdAtTs, images };
};

const albumImageKey = (img) => {
  if (!img) return "";
  if (img.id) return `id:${img.id}`;
  if (img.messageRequestId) return `req:${img.messageRequestId}`;
  const fileUrl = Array.isArray(img.fileUrl) ? img.fileUrl[0] : img.fileUrl;
  if (fileUrl) return `url:${fileUrl}`;
  if (img.url) return `url:${img.url}`;
  return "";
};

const dedupeAlbumImages = (images) => {
  if (!Array.isArray(images) || images.length < 2) return images || [];
  const seen = new Set();
  const out = [];
  for (const img of images) {
    const key = albumImageKey(img);
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(img);
  }
  return out;
};

// Union the `userEmoji` reactions across every image in an album, deduped by
// sender+emoji (the BE allows one reaction per member per emoji). The album
// "anchor" message differs across platforms (web keeps the OLDEST image, the
// mobile client the NEWEST), so a reaction posted from mobile lands on a
// different image id than the one web renders. Without unioning, that reaction
// never shows on web. Reads each image's own `userEmoji` plus an optional base
// (the rendered row's current `userEmoji`).
const unionAlbumUserEmoji = (images, base) => {
  const seen = new Set();
  const merged = [];
  const senderKey = (s) => (s && typeof s === "object" ? s.id : s);
  const push = (arr) => {
    (arr || []).forEach((e) => {
      if (!e) return;
      const k = senderKey(e.sender) + "|" + e.emoji;
      if (seen.has(k)) return;
      seen.add(k);
      merged.push(e);
    });
  };
  push(base);
  (images || []).forEach((im) => push(im && im.userEmoji));
  return merged;
};

export const buildRenderableSections = (rawSections = [], formatDateFn = null) => {
  if (!Array.isArray(rawSections)) {
    return [];
  }

  const flat = [];
  rawSections.forEach((sec) => {
    if (!sec || !Array.isArray(sec.items)) return;
    sec.items.forEach((item) => {
      // Deletes are only allowed within 10 minutes; hide the bubble instead
      // of leaving a "This message has been deleted" placeholder.
      // Rows without createdAt, type or text are not messages (e.g. an old
      // send-response string cached as a row) and would render as an empty bubble.
      const phantom = item && !item.createdAt && !item.messageType && item.message == null;
      if (item && !item.isDeleted && !phantom) {
        flat.push({ ...item, _sectionDate: sec.createdAt });
      }
    });
  });

  if (flat.length === 0) {
    return [];
  }

  const normalized = flat.map(normalizeFlatMessage).filter(Boolean);

  normalized.sort((a, b) => {
    const diff = (a.createdAtTs || 0) - (b.createdAtTs || 0);
    if (diff !== 0) return diff;
    const ar = a.messageRequestId || "";
    const br = b.messageRequestId || "";
    if (ar !== br) return ar.localeCompare(br);
    const ai = a.id || "";
    const bi = b.id || "";
    return ai.localeCompare(bi);
  });

  const grouped = [];
  normalized.forEach((item) => {
    if (!item) return;

    const hasText = typeof item.message === "string" && item.message.trim() !== "";

    if (item.messageType === "doc") {
      if (groupConsecutiveDocs(grouped, item)) return;
      grouped.push(canJoinDocAlbum(item) ? { ...item, documents: [item] } : item);
      return;
    }

    if (item.messageType !== "image") {
      grouped.push(item);
      return;
    }

    if (grouped.length > 0 && grouped[grouped.length - 1]?.messageType === "image") {
      const last = grouped[grouped.length - 1];
      if (!last) return;

      const lastHasText = typeof last.message === "string" && last.message.trim() !== "";
      const sameSender = idsMatch(last.senderId, item.senderId);
      const lastTs = last.createdAtTs || last.createdAt ? new Date(last.createdAt).getTime() : 0;
      const itemTs = item.createdAtTs || item.createdAt ? new Date(item.createdAt).getTime() : 0;

      const sameAlbum = isSameMediaSend(last, item, lastTs, itemTs);
      const sharedAlbumId = getMediaAlbumId(last) || getMediaAlbumId(item);
      // Album id: merge the whole send, including the captioned last file.
      // Legacy (no id): keep the old rule — only silent images join via the 1-minute window.
      if (
        sameSender &&
        sameAlbum &&
        (sharedAlbumId || (!lastHasText && !hasText))
      ) {
        const lastImages = Array.isArray(last.images) ? last.images : [];
        const itemImages = Array.isArray(item.images) ? item.images : [];
        const albumId = sharedAlbumId || last.mediaAlbumId || item.mediaAlbumId;
        const mergedImages = dedupeAlbumImages([...lastImages, ...itemImages])
          .map((img) => (albumId && !img.mediaAlbumId ? { ...img, mediaAlbumId: albumId } : img))
          .sort(
          (a, b) => {
            const aTs = a.createdAtTs || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const bTs = b.createdAtTs || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return aTs - bTs;
          }
        );
        grouped[grouped.length - 1] = {
          ...last,
          isMultiples: true,
          count: mergedImages.length,
          images: mergedImages,
          createdAtTs: Math.min(lastTs, itemTs),
          mediaAlbumId: albumId || last.mediaAlbumId || item.mediaAlbumId,
          message: hasText ? item.message : last.message,
        };
        return;
      }
    }

    const uniqueImages = dedupeAlbumImages(item.images);
    const sortedImages =
      Array.isArray(uniqueImages) && uniqueImages.length > 1
        ? [...uniqueImages].sort((a, b) => {
            const aTs = a.createdAtTs || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const bTs = b.createdAtTs || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return aTs - bTs;
          })
        : uniqueImages;
    grouped.push({ ...item, images: sortedImages });
  });

  const final = [];
  grouped.forEach((item) => {
    const hasText = typeof item.message === "string" && item.message.trim() !== "";

    if (item.messageType === "doc" && Array.isArray(item.documents)) {
      // A lone document keeps the exact shape it had before grouping so the
      // single-file bubble and the message actions stay untouched.
      if (item.documents.length < 2) {
        const { documents, ...rest } = item;
        final.push(rest);
      } else {
        final.push(item);
      }
      return;
    }

    if (item.messageType === "image" && item.images && item.images.length > 1 && hasText) {
      const imgs = [...item.images].sort((a, b) => (a.createdAtTs || 0) - (b.createdAtTs || 0));
      const lastImg = imgs[imgs.length - 1];
      const galleryImgs = imgs.slice(0, imgs.length - 1);
      const baseTimestamp = item.createdAtTs || (item.createdAt ? new Date(item.createdAt).getTime() : Date.now());

      if (galleryImgs.length > 0) {
        final.push({
          ...item,
          message: "",
          previewMessage: item.message,
          images: galleryImgs,
          isMultiples: true,
          hasMultiple: true,
          isLastImage: false,
          count: galleryImgs.length,
          createdAt: new Date(baseTimestamp - 1).toISOString(),
          createdAtTs: baseTimestamp - 1,
          orderHint: 0,
        });
      }

      final.push({
        ...item,
        previewMessage: item.message,
        images: [lastImg],
        isMultiples: false,
        hasMultiple: false,
        isLastImage: true,
        count: 1,
        createdAt: new Date(baseTimestamp).toISOString(),
        createdAtTs: baseTimestamp,
        orderHint: 1,
      });
    } else {
      final.push(item);
    }
  });

  // Album bubbles must display the UNION of reactions across all their images
  // (see unionAlbumUserEmoji). Done as a final pass so it covers every album
  // shape produced above regardless of how it was grouped/split.
  final.forEach((m) => {
    if (m && m.isMultiples && Array.isArray(m.images) && m.images.length > 1) {
      m.userEmoji = unionAlbumUserEmoji(m.images, m.userEmoji);
    }
    if (m && m.isMultipleDocs && Array.isArray(m.documents) && m.documents.length > 1) {
      m.userEmoji = unionAlbumUserEmoji(m.documents, m.userEmoji);
    }
  });

  const byDate = {};
  final.forEach((m) => {
    const dateStr = formatDateFn
      ? formatDateFn(m.createdAtTs || m.createdAt || Date.now())
      : moment(m.createdAtTs || m.createdAt || Date.now()).format("D MMM, Y");
    if (!byDate[dateStr]) byDate[dateStr] = [];
    byDate[dateStr].push(m);
  });

  return Object.keys(byDate)
    .sort((a, b) => new Date(a) - new Date(b))
    .map((createdAt) => ({
      createdAt,
      items: byDate[createdAt].sort((a, b) => {
        const aTs = a.createdAtTs || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const bTs = b.createdAtTs || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return aTs - bTs;
      }),
    }));
};

const _normalizePersonName = (n) => String(n || "").replace(/\s+/g, " ").trim().toLowerCase();

const _isCurrentUserName = (name, user) => {
  if (!name || !user) return false;
  const n = _normalizePersonName(name);
  const withTitle = _normalizePersonName(
    [user.title, user.firstName, user.lastName].filter(Boolean).join(" ")
  );
  const withoutTitle = _normalizePersonName(
    [user.firstName, user.lastName].filter(Boolean).join(" ")
  );
  return (withTitle && n === withTitle) || (withoutTitle && n === withoutTitle);
};

const _otherUserFromGroup = (chat, user) => {
  const users = chat?.group?.users;
  if (!Array.isArray(users) || user?.id == null) return null;
  return users.find((u) => u && String(u.id || u) !== String(user.id)) || null;
};

/**
 * Direct-message list rows must always show the other person, never whoever
 * sent the last message.
 *
 * getUserChats encodes:
 *   senderAvatar / senderName = last-message sender
 *   avatar / name             = the other participant
 * Optimistic list updates change senderId without swapping those fields, so
 * we also accept whichever field is not the current user.
 */
export const isSelfChatGroup = (chat) => {
  if (chat?.group?.isSelfChat || chat?.isSelfChat) return true;
  // Only My Space can be pinned, so a pinned row is My Space even when the
  // API omits isSelfChat (the mobile list keys off this).
  if (chat?.group?.isPinned) return true;
  // Fallback: self-chat is the only 1:1 with a single member (the owner).
  const users = chat?.group?.users;
  return !!(chat?.group?.isOneToOne && Array.isArray(users) && users.length === 1);
};

/** DMs tab: regular 1:1 chats plus My Space (self-chat). */
export const isDirectMessageChat = (chat) =>
  !!(chat?.group?.isOneToOne || chat?.isOneToOne || isSelfChatGroup(chat));

const _selfAvatar = (user, chat) => {
  const fromUsers = (chat?.group?.users || []).find(
    (u) => u && String(u.id || u) === String(user?.id)
  );
  return (
    user?.avatar ||
    user?.img ||
    user?.profilePicture ||
    fromUsers?.avatar ||
    chat?.avatar ||
    chat?.senderAvatar ||
    null
  );
};

export const getOneToOnePartnerAvatar = (chat, user) => {
  if (!chat) return null;
  if (isSelfChatGroup(chat)) {
    return _selfAvatar(user, chat);
  }
  const fromUsers = _otherUserFromGroup(chat, user);
  if (fromUsers?.avatar) return fromUsers.avatar;

  const myAvatar = user?.avatar || null;
  const iSentLast = user?.id != null && String(chat.senderId) === String(user.id);
  const encodedPartner = iSentLast ? chat.avatar : chat.senderAvatar;
  const encodedSelf = iSentLast ? chat.senderAvatar : chat.avatar;

  if (encodedPartner && encodedPartner !== myAvatar) return encodedPartner;
  if (encodedSelf && encodedSelf !== myAvatar) return encodedSelf;
  if (encodedPartner !== myAvatar) return encodedPartner || null;
  return encodedSelf && encodedSelf !== myAvatar ? encodedSelf : null;
};

export const getOneToOnePartnerName = (chat, user, selfChatLabel) => {
  if (!chat) return "";
  if (isSelfChatGroup(chat)) return selfChatLabel || "My Space";
  const fromUsers = _otherUserFromGroup(chat, user);
  if (fromUsers?.fullName) return fromUsers.fullName;

  const iSentLast = user?.id != null && String(chat.senderId) === String(user.id);
  const encoded = iSentLast ? chat.name : chat.senderName;
  const alt = iSentLast ? chat.senderName : chat.name;

  if (encoded && !_isCurrentUserName(encoded, user)) return encoded;
  if (alt && !_isCurrentUserName(alt, user)) return alt;
  if (chat.group?.name && !_isCurrentUserName(chat.group.name, user)) return chat.group.name;
  return encoded || alt || chat.group?.name || "";
};

export const isArchivedChat = (chat) => {
  if (!chat || isSelfChatGroup(chat)) return false;
  return !!(chat.isArchived || chat.group?.isArchived);
};

export const excludeArchivedChats = (chats) =>
  (chats || []).filter((chat) => !isArchivedChat(chat));

/**
 * Last remaining message in a conversation after some rows are deleted.
 * Used to refresh the chat-list preview so it never shows the deleted placeholder.
 */
export const getLastVisibleMessagePreview = (sections, excludeIds = []) => {
  const excluded = new Set((excludeIds || []).map((id) => String(id)));
  const items = [];
  (sections || []).forEach((sec) => {
    (sec.items || []).forEach((item) => {
      if (!item || item.isDeleted || item.userDeleted) return;
      if (excluded.has(String(item.id))) return;
      items.push(item);
    });
  });
  if (items.length === 0) {
    return {
      lastMessageDeleted: false,
      previewMessage: "",
      message: "",
      originalMessage: "",
    };
  }
  items.sort((a, b) => {
    const aTs = a.createdAtTs || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
    const bTs = b.createdAtTs || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
    return aTs - bTs;
  });
  const last = items[items.length - 1];
  const text = (last.previewMessage || last.message || "").toString();
  return {
    lastMessageDeleted: false,
    previewMessage: text,
    message: text,
    originalMessage: last.originalMessage || text,
    messageType: last.messageType || "simple",
    createdAt: last.createdAt,
  };
};

export const applyVisiblePreviewToChat = (chat, preview) => ({
  ...chat,
  lastMessageDeleted: false,
  previewMessage: preview?.previewMessage || "",
  message: preview?.message || "",
  originalMessage: preview?.originalMessage || "",
  messageType: preview?.messageType || chat?.messageType,
  createdAt: preview?.createdAt || chat?.createdAt,
});

/**
 * Group-chat bubble label: first + last name only (no job title).
 * Cached /getChat rows may still include title in senderName; member
 * first/last name is the source of truth when present.
 */
export const groupBubbleSenderName = (chat, members) => {
  if (!chat) return "";
  const list = Array.isArray(members) ? members : [];
  const member = list.find((u) => u && String(u.id) === String(chat.senderId));
  if (member) {
    const fromParts = `${member.firstName || ""} ${member.lastName || member.lastname || ""}`.trim();
    if (fromParts) return fromParts;
    const title = String(member.title || "").trim();
    const full = String(member.fullName || member.name || "").trim();
    if (full && title && full.toLowerCase().startsWith(title.toLowerCase())) {
      return full.slice(title.length).trim() || full;
    }
    if (full) return full;
  }
  const raw = String(chat.senderName || "").trim();
  if (!raw) return "";
  const title = member ? String(member.title || "").trim() : "";
  if (title && raw.toLowerCase().startsWith(title.toLowerCase())) {
    return raw.slice(title.length).trim() || raw;
  }
  return raw;
};
